/* eslint-disable no-console, @typescript-eslint/no-explicit-any */
/**
 * Hosted folder access — the pane side of the shell's fs bridge.
 *
 * When an app runs as a cross-origin iframe inside a host (hub or kor), Chromium blocks
 * `showDirectoryPicker()` ("Cross origin sub frames aren't allowed to show a
 * file picker") and blocks handle `requestPermission()` too. There is no way
 * for this document to own a directory handle, so the adapter does the next
 * best thing: it asks the host (top frame) to pick a folder and to run every
 * file operation inside the host's own document, over the pane bridge
 * (`fs:pick` / `fs:restore` / `fs:forget` / `fs:call`).
 *
 * The bridge is only armed when this document is actually hosted: the host
 * pins its origin to the address it embeds (`host=` query parameter), which is
 * also the only origin replies are accepted from. Standalone apps never load this path.
 */

import type { FileSystemAdapter } from './types';
import { ACCEPTED_HOST_IDS, PROTOCOL_VERSION } from './protocol';
import { SUITE_SHELF_FOLDER } from './shelf';

/** The query parameter the host uses to pin its origin into an embedded document. */
const HOST_PARAM = 'host';

/** Ordinary file operations; generous, because the host may stop to ask for permission. */
const OP_TIMEOUT_MS = 120_000;

/**
 * Origin the host declared for itself, or null when this document is not hosted.
 * Exported for use by other modules (e.g., Bak's hostMode.ts).
 */
export function declaredHostOrigin(): string | null {
    try {
        const value = new URLSearchParams(window.location.search).get(HOST_PARAM);
        if (!value) return null;
        const origin = new URL(value).origin;
        // Only http(s) origins are meaningful postMessage targets.
        return origin.startsWith('http://') || origin.startsWith('https://') ? origin : null;
    } catch {
        return null;
    }
}

/** True when this document runs inside a host with a bridgeable host origin. */
export function isHostedWithBridge(): boolean {
    try {
        return window.self !== window.top && declaredHostOrigin() !== null;
    } catch {
        // A thrown cross-origin access check means this frame IS embedded.
        return declaredHostOrigin() !== null;
    }
}

interface PendingCall {
    resolve: (value: any) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout> | null;
}

/**
 * One module-level RPC channel. A single listener serves every adapter
 * instance: replies are matched by their `req` counter, not by which adapter
 * asked, so opening several folders over a session never stacks listeners.
 */
class HostBridgeRpc {
    private readonly origin: string;
    private readonly appId: string;
    private readonly pending = new Map<number, PendingCall>();
    private seq = 0;

    constructor(origin: string, appId: string) {
        this.origin = origin;
        this.appId = appId;
        window.addEventListener('message', this.onMessage);
    }

    private onMessage = (event: MessageEvent) => {
        if (event.source !== window.parent || event.origin !== this.origin) return;
        const data = event.data as { src?: string; type?: unknown; req?: unknown } | null;
        if (!data || typeof data !== 'object') return;
        // Accept replies from any known host ('hat' or 'kor')
        if (!ACCEPTED_HOST_IDS.includes(data.src as any)) return;
        if (typeof data.type !== 'string' || !data.type.startsWith('host:fs:')) return;
        const req = typeof data.req === 'number' ? data.req : -1;
        const call = this.pending.get(req);
        if (!call) return;
        this.pending.delete(req);
        if (call.timer !== null) clearTimeout(call.timer);
        const ok = (event.data as { ok?: unknown }).ok === true;
        if (ok) {
            const payload = event.data as { result?: unknown; name?: string };
            // Pick/restore answers carry the folder in `name`; calls in `result`.
            call.resolve(payload.result !== undefined ? payload.result : payload.name);
        } else {
            const error = (event.data as { error?: unknown }).error;
            call.reject(new Error(typeof error === 'string' && error ? error : 'Folder request failed'));
        }
    };

    /** Fire one bridge request and await its reply. No timeout when 0. */
    request<T>(type: string, fields: Record<string, unknown>, timeoutMs = 0): Promise<T> {
        const req = ++this.seq;
        return new Promise<T>((resolve, reject) => {
            const call: PendingCall = { resolve, reject, timer: null };
            if (timeoutMs > 0) {
                call.timer = setTimeout(() => {
                    this.pending.delete(req);
                    reject(new Error(`Folder bridge request "${type}" timed out`));
                }, timeoutMs);
            }
            this.pending.set(req, call);
            window.parent.postMessage(
                { v: PROTOCOL_VERSION, src: this.appId, dst: 'hat', type, req, ...fields },
                this.origin
            );
        });
    }
}

let rpc: HostBridgeRpc | null = null;

function bridgeRpc(appId: string): HostBridgeRpc {
    if (!rpc) {
        const origin = declaredHostOrigin();
        if (!origin) throw new Error('Folder bridge is not available outside the host');
        rpc = new HostBridgeRpc(origin, appId);
    }
    return rpc;
}

/**
 * The subset of `WebAdapter`'s surface `FolderManager` drives, shared by the
 * real web adapter and the bridge adapter so the open-folder flow needs no
 * per-mode branching beyond construction.
 */
export interface WebFolderAdapter extends FileSystemAdapter {
    setFolderId(id: string): void;
    setDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void>;
    initialize(): Promise<void>;
    initializeWithStoredHandleOnly(expectedDirectoryName?: string): Promise<boolean>;
    isReady(): boolean;
    getDirectoryName(): Promise<string | null>;
    getFile(path: string): Promise<File>;
}

/**
 * Folder adapter for a document hosted in a host: every operation is
 * proxied to the host, which owns the granted directory handle.
 */
export class HostBridgeAdapter implements WebFolderAdapter {
    /** The granted root this adapter is bound to (the folder's own name). */
    private folderId: string | null = null;
    private isInitialized = false;
    /** Writes (and every op, for simplicity) run one at a time, in call order. */
    private queue: Promise<unknown> = Promise.resolve();

    constructor(
        private readonly appId: string
    ) {}

    setFolderId(id: string): void {
        this.folderId = id;
    }

    /**
     * A pre-obtained handle cannot exist in a hosted document (the picker is
     * blocked here by design), so there is nothing to adopt. Kept for surface
     * parity with WebAdapter; a handle reaching this method means a flow ran
     * outside the hosted contract.
     */
    async setDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
        console.warn('[HostBridgeAdapter] setDirectoryHandle is not supported in hosted mode, ignoring:', handle?.name);
    }

    /**
     * New folder selection: the host shows the picker (with its own consent
     * fallback when this frame's click does not carry a gesture) and keeps the
     * granted handle. The folder binds to the picked directory's own name.
     */
    async initialize(): Promise<void> {
        const name = await bridgeRpc(this.appId).request<string>('fs:pick', {});
        this.folderId = name;
        this.isInitialized = true;
        console.log('[HostBridgeAdapter] Host picked folder:', name);
    }

    /**
     * Reattach a folder granted earlier without showing a picker. Returns
     * false when the host holds no such grant; permission that only needs a
     * gesture is settled lazily by the first file operation.
     */
    async initializeWithStoredHandleOnly(expectedDirectoryName?: string): Promise<boolean> {
        const expected = expectedDirectoryName || this.folderId || undefined;
        try {
            const name = await bridgeRpc(this.appId).request<string>('fs:restore', { name: expected });
            this.folderId = name || expected || null;
            this.isInitialized = true;
            return true;
        } catch (e) {
            console.warn('[HostBridgeAdapter] Restore failed:', (e as Error).message);
            return false;
        }
    }

    isReady(): boolean {
        return this.isInitialized;
    }

    async getDirectoryName(): Promise<string | null> {
        if (this.folderId) return this.folderId;
        try {
            return await this.call<string>('dirname', []);
        } catch {
            return null;
        }
    }

    /** Serialize every operation: the host's handle is a shared, order-sensitive resource. */
    private call<T>(op: string, args: unknown[]): Promise<T> {
        const run = async (): Promise<T> => {
            if (!rpc && !isHostedWithBridge()) throw new Error('Folder bridge is not available outside the host');
            return bridgeRpc(this.appId).request<T>(
                'fs:call',
                { op, args, folder: this.folderId ?? undefined },
                OP_TIMEOUT_MS
            );
        };
        const next = this.queue.then(run, run);
        // Keep the chain alive regardless of individual failures.
        this.queue = next.catch(() => undefined);
        return next;
    }

    /* ------------------------- FileSystemAdapter ------------------------- */

    async read(path: string): Promise<string> {
        return this.call<string>('read', [path]);
    }

    async readFile(path: string): Promise<string> {
        return this.read(path);
    }

    async readBinary(path: string): Promise<Uint8Array> {
        const bytes = await this.call<ArrayBuffer | Uint8Array>('readBinary', [path]);
        return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    }

    async getFile(path: string): Promise<File> {
        const bytes = await this.readBinary(path);
        const name = path.split('/').filter((p) => p).pop() || 'file';
        return new File([bytes as BlobPart], name);
    }

    async write(path: string, data: string): Promise<void> {
        await this.call<void>('write', [path, data]);
    }

    async writeBinary(path: string, data: Uint8Array | ArrayBuffer | Blob): Promise<void> {
        // Blobs have no wire format across postMessage; read them into bytes first.
        const bytes = data instanceof Blob ? new Uint8Array(await data.arrayBuffer()) : data;
        await this.call<void>('writeBinary', [path, bytes]);
    }

    async list(path: string): Promise<string[]> {
        return this.call<string[]>('list', [path]);
    }

    async listDetailed(path: string): Promise<Array<{ name: string; path: string; isFile: boolean; isDirectory: boolean; mtime: number; ctime: number; size: number }>> {
        return this.call<Array<{ name: string; path: string; isFile: boolean; isDirectory: boolean; mtime: number; ctime: number; size: number }>>('listDetailed', [path]);
    }

    async exists(path: string): Promise<boolean> {
        return this.call<boolean>('exists', [path]);
    }

    async mkdir(path: string): Promise<void> {
        await this.call<void>('mkdir', [path]);
    }

    async rm(path: string): Promise<void> {
        await this.call<void>('rm', [path]);
    }

    async stat(path: string): Promise<{ isFile: boolean; isDirectory: boolean; mtime: number; size: number; ctime: number }> {
        return this.call<{ isFile: boolean; isDirectory: boolean; mtime: number; size: number; ctime: number }>('stat', [path]);
    }

    async rename(oldPath: string, newPath: string): Promise<void> {
        await this.call<void>('rename', [oldPath, newPath]);
    }
}

/**
 * The folder adapter factory for web/open-folder flows: the bridge adapter
 * when this document is hosted in a host, the real WebAdapter
 * (browser File System Access API) everywhere else.
 *
 * @param appId - The application id for bridge messages (e.g., 'kor', 'bak', 'yaz')
 * @param webAdapterFactory - Factory function to create a WebAdapter when not hosted
 */
export function createWebFolderAdapter(
    appId: string,
    webAdapterFactory: () => WebFolderAdapter
): WebFolderAdapter {
    if (isHostedWithBridge()) {
        console.log('[HostBridgeAdapter] Hosted document detected, using host folder bridge');
        return new HostBridgeAdapter(appId);
    }
    return webAdapterFactory();
}

/**
 * Ask the host to reattach the folder it granted most recently for this
 * application (no name: the host answers with the last root it still holds).
 * Resolves the granted directory name, or null when nothing is granted or this
 * document is not hosted — the boot-time fallback for when this document's own
 * folder registry is empty but the host still remembers the grant.
 */
export async function restoreHostedFolderName(appId: string): Promise<string | null> {
    if (!isHostedWithBridge()) return null;
    try {
        // The bridge timeout (see initializeWithStoredHandleOnly): a restore
        // that no host answers resolves to null instead of hanging the boot.
        const name = await bridgeRpc(appId).request<string>('fs:restore', {}, OP_TIMEOUT_MS);
        return typeof name === 'string' && name ? name : null;
    } catch {
        return null;
    }
}

/**
 * The suite shelf — a synthetic folder every hosted pane shares with its host,
 * needing neither a picker nor a permission grant. Applications that own no
 * disk files (sak's vault is server-backed) stage payloads onto it, and the
 * editors open/save them through this adapter: the same `fs:call` vocabulary,
 * with the synthetic folder name selecting the host's own shelf store.
 */
class SuiteShelfAdapter extends HostBridgeAdapter {
    constructor(appId: string) {
        super(appId);
        this.setFolderId(SUITE_SHELF_FOLDER);
    }

    /** The shelf is always available while hosted — there is nothing to grant. */
    async initialize(): Promise<void> {}

    async initializeWithStoredHandleOnly(expectedDirectoryName?: string): Promise<boolean> {
        void expectedDirectoryName;
        return true;
    }

    isReady(): boolean {
        return true;
    }

    async getDirectoryName(): Promise<string | null> {
        return SUITE_SHELF_FOLDER;
    }
}

export function createSuiteShelfAdapter(appId: string): WebFolderAdapter {
    return new SuiteShelfAdapter(appId);
}
